/**
 * <ProposalDeck /> — multi-slide pricing proposal.
 *
 * Used on /sales/pricing (rep preview) and on the public /r share page.
 * Print stylesheet treats each .sp-slide as a page; Export PDF uses the
 * browser print dialog (same pattern as Deal Brief / Discovery Report).
 *
 * Numbers come from the live quote + catalog snapshot (Pricing-bot USD).
 * Scale and Enterprise render as custom quote — never an invented list.
 */
import type { ReactNode } from 'react';
import MoxoLogo from '../sales-enablement/MoxoLogo';
import {
  addonsOf,
  annualize,
  capabilityMatrix,
  catalogMatchesQuotedSchema,
  currencyLabel,
  editionCapacityLine,
  editionForQuote,
  editionPlanPriceMinor,
  editionPlanSubtitle,
  planFeatureColumns,
  formatInvestmentCell,
  formatLongDate,
  formatMonthYear,
  formatValidUntil,
  investmentFinePrint,
  investmentSummary,
  lineOf,
  money,
  perFlowMinor,
  platformLine,
  proposalHasAiJobBilling,
  quoteHasUnlimitedSeats,
  quotedServiceLines,
  seatTermsLabel,
  selectedIncludes,
  showEnterpriseServices,
} from './proposal-deck-model';
import {
  catalogHasLegacySeatEditions,
  defaultValidUntil,
  editionIncludedFlows,
  isLegacySeatEdition,
  normalizeProposalForDeck,
  type PricingProposal,
  type ProposalEditionSnapshot,
} from './proposal-types';

export default function ProposalDeck({
  proposal: rawProposal,
  showActions = true,
}: {
  proposal: PricingProposal;
  showActions?: boolean;
}) {
  const proposal = normalizeProposalForDeck(rawProposal);
  const { quote, prospect, catalogVersion, generatedAt, note, preparedBy, validUntil } = proposal;
  const currency = quote.currency || proposal.catalog?.currency || 'USD';
  const cur = currencyLabel(currency);
  const year = new Date(generatedAt).getFullYear();
  const validLabel = formatValidUntil(validUntil || defaultValidUntil(generatedAt));
  const preparedDate = formatMonthYear(generatedAt);
  const catalogOk = catalogMatchesQuotedSchema(proposal);
  const editions = catalogOk ? (proposal.catalog?.editions ?? []) : [];
  const selected = catalogOk ? editionForQuote(proposal) : undefined;
  const includes = catalogOk ? selectedIncludes(proposal) : [];
  const columns = planFeatureColumns(selected, includes);
  const platform = platformLine(quote);
  const platformAnnual = annualize(platform, quote.termYears || 1);
  const includedAi = selected?.includedAiSpendAnnualMinor ?? null;
  const aiLine = lineOf(quote, 'ai_credits');
  const selectedIsLegacy = selected != null && isLegacySeatEdition(selected);
  const includedFlows = editionIncludedFlows(selected);
  const aiAnnual = selectedIsLegacy ? null : includedAi ?? annualize(aiLine, quote.termYears || 1);
  const flowsCustom = selected != null && !selectedIsLegacy && selected.includedFlowsAnnual == null;
  const aiCustom = selected != null && !selectedIsLegacy && selected.includedAiSpendAnnualMinor == null && !aiLine;
  const legacyCatalog = catalogOk && catalogHasLegacySeatEditions(proposal.catalog);
  const usageAddons = catalogOk ? addonsOf(proposal.catalog, 'usage') : [];
  const aiAddons = catalogOk ? addonsOf(proposal.catalog, 'ai_credits') : [];
  const portalAddons = catalogOk ? addonsOf(proposal.catalog, 'portal') : [];
  const serviceLines = quotedServiceLines(proposal);
  const summary = investmentSummary(proposal);
  const unlimitedSeats = quoteHasUnlimitedSeats(proposal);
  const aiJobBilling = proposalHasAiJobBilling(proposal);
  const provenAiJobs = aiJobBilling ? proposal.catalog?.aiJobs : undefined;
  const seatTerms = seatTermsLabel(proposal);
  const matrix = editions.length > 0 ? capabilityMatrix(editions) : [];
  const confidential = `© MOXO INC ${year} / CONFIDENTIAL`;

  return (
    <article className="sp-deck se-report" data-proposal-print>
      <CoverSlide
        company={prospect.companyName}
        contact={prospect.contactName}
        preparedBy={preparedBy}
        preparedDate={preparedDate}
        validLabel={validLabel}
        confidential={confidential}
      />

      {editions.length > 0 && (
        <PlansOverviewSlide
          editions={editions}
          selectedId={quote.input.editionId}
          currency={currency}
          cur={cur}
          confidential={confidential}
          legacyCatalog={legacyCatalog}
          quoteUnlimitedSeats={unlimitedSeats}
        />
      )}

      <SelectedPlanSlide
        company={prospect.companyName}
        editionName={quote.editionName}
        cur={cur}
        validLabel={validLabel}
        platformAnnual={platformAnnual}
        currency={currency}
        flows={includedFlows}
        flowsCustom={flowsCustom}
        aiAnnual={aiAnnual}
        aiCustom={aiCustom}
        aiName={
          selectedIsLegacy
            ? 'Published AI packs'
            : includedAi != null
              ? 'AI spend included'
              : aiLine?.name
        }
        termMonths={quote.input.termMonths}
        seatTerms={seatTerms}
        columns={columns}
        confidential={confidential}
      />

      {matrix.length > 0 && (
        <PlatformGridSlide editions={editions} matrix={matrix} confidential={confidential} />
      )}

      <IncludesSlide
        cards={
          unlimitedSeats && catalogOk && proposal.catalog?.everyPlanIncludes
            ? proposal.catalog.everyPlanIncludes
            : unlimitedSeats
              ? [
                  { title: 'Unlimited team members', body: 'No per seat charges. Invite the entire org.' },
                  { title: 'Process + form builders', body: 'Conversational builders for flows and forms.' },
                  { title: 'Reports', body: 'Conversational command surface for analytics.' },
                  { title: 'Tests', body: 'Validate every flow before it goes live.' },
                ]
              : [
                  { title: 'Process + form builders', body: 'Conversational builders for flows and forms.' },
                  { title: 'Reports', body: 'Conversational command surface for analytics.' },
                  { title: 'Tests', body: 'Validate every flow before it goes live.' },
                ]
        }
        unlimitedSeats={unlimitedSeats}
        aiJobs={provenAiJobs}
        currency={currency}
        confidential={confidential}
      />

      {(usageAddons.length > 0 || aiAddons.length > 0) && (
        <AddonsSlide
          usageAddons={usageAddons}
          aiAddons={aiAddons}
          portalAddons={portalAddons}
          currency={currency}
          confidential={confidential}
        />
      )}

      {provenAiJobs && (
        <AiJobsSlide aiJobs={provenAiJobs} currency={currency} confidential={confidential} />
      )}

      {showEnterpriseServices(proposal) && serviceLines.length > 0 && (
        <ServicesSlide lines={serviceLines} currency={currency} confidential={confidential} />
      )}

      <InvestmentSlide
        company={prospect.companyName}
        cur={cur}
        validLabel={validLabel}
        summary={summary}
        currency={currency}
        confidential={confidential}
      />

      {(note || quote.adjustments.length > 0) && (
        <NotesSlide
          note={note}
          adjustments={quote.adjustments}
          math={quote.math[quote.math.length - 1]}
          catalogVersion={catalogVersion}
          effectiveDate={quote.effectiveDate}
          preparedWhen={formatLongDate(generatedAt)}
          confidential={confidential}
        />
      )}

      <p className="sp-deck__catalog-foot">
        Catalog {catalogVersion} · effective list as of {quote.effectiveDate} · prepared{' '}
        {formatLongDate(generatedAt)}. This is a proposal, not an invoice. Custom SKUs go through
        Finance — published rates only.
        {showActions && quote.oneTimeNetMinor > 0
          ? ` One-time implementation net ${money(quote.oneTimeNetMinor, currency)} is included in the term total.`
          : ''}
      </p>
    </article>
  );
}

function SlideChrome({
  eyebrow,
  validLabel,
  confidential,
  children,
}: {
  eyebrow?: string;
  validLabel?: string;
  confidential: string;
  children: ReactNode;
}) {
  return (
    <section className="sp-slide">
      <header className="sp-slide__top">
        <p className="sp-slide__eyebrow">{eyebrow}</p>
        <div className="sp-slide__top-right">
          {validLabel && <p className="sp-slide__valid">Pricing valid until {validLabel}</p>}
          <span className="sp-orb" aria-hidden="true" />
        </div>
      </header>
      <div className="sp-slide__body">{children}</div>
      <footer className="sp-slide__foot">
        <span />
        <span>{confidential}</span>
      </footer>
    </section>
  );
}

function CoverSlide({
  company,
  contact,
  preparedBy,
  preparedDate,
  validLabel,
  confidential,
}: {
  company: string;
  contact?: string;
  preparedBy?: string;
  preparedDate: string;
  validLabel: string;
  confidential: string;
}) {
  return (
    <section className="sp-slide sp-slide--cover">
      <div className="sp-cover">
        <div className="sp-cover__copy">
          <MoxoLogo size={36} />
          <p className="sp-cover__for">Prepared for</p>
          <h2 className="sp-cover__co">{company}</h2>
          <p className="sp-cover__kind">Commercial Proposal</p>
          {contact && <p className="sp-cover__meta">Attention {contact}</p>}
          {preparedBy && <p className="sp-cover__meta">Prepared by {preparedBy}</p>}
          <p className="sp-cover__meta">{preparedDate}</p>
          <p className="sp-slide__valid sp-cover__valid">Pricing valid until {validLabel}</p>
        </div>
        <div className="sp-cover__art" aria-hidden="true">
          <span className="sp-orb sp-orb--lg" />
        </div>
      </div>
      <footer className="sp-slide__foot">
        <span />
        <span>{confidential}</span>
      </footer>
    </section>
  );
}

function PlansOverviewSlide({
  editions,
  selectedId,
  currency,
  cur,
  confidential,
  legacyCatalog,
  quoteUnlimitedSeats,
}: {
  editions: ProposalEditionSnapshot[];
  selectedId: string;
  currency: string;
  cur: string;
  confidential: string;
  legacyCatalog: boolean;
  quoteUnlimitedSeats: boolean;
}) {
  return (
    <SlideChrome eyebrow={`Moxo plans (${cur})`} confidential={confidential}>
      <h3 className="sp-slide__title">Three plans. One platform.</h3>
      <div className="sp-plans">
        {editions.map((ed, i) => {
          const dark = i === editions.length - 1;
          return (
            <article
              key={ed.id}
              className={
                'sp-plan' +
                (dark ? ' sp-plan--dark' : '') +
                (ed.id === selectedId ? ' is-selected' : '')
              }
            >
              <p className="sp-plan__name">{ed.name}</p>
              <p className="sp-plan__price">
                {ed.listIsFrom ? 'from ' : ''}
                {(() => {
                  const price = editionPlanPriceMinor(ed);
                  if (price != null) return money(price, currency);
                  return ed.customQuote || ed.listAnnualMinor == null ? 'Custom quote' : '—';
                })()}
              </p>
              <p className="sp-plan__sub">{editionPlanSubtitle(ed, cur, quoteUnlimitedSeats)}</p>
              <p className="sp-plan__cap">{editionCapacityLine(ed, currency)}</p>
              <ul className="sp-plan__list">
                {ed.included.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>
      <div className="sp-chargebar">
        <p className="sp-chargebar__kicker">How Moxo charges</p>
        <div className="sp-chargebar__cols">
          <div>
            <strong>Platform fee</strong>
            <span>{legacyCatalog ? 'Named-seat software plan' : 'Yearly software plan'}</span>
          </div>
          <div>
            <strong>+ Flow capacity</strong>
            <span>{legacyCatalog ? 'Included runs per year' : 'Flow runs per year'}</span>
          </div>
          <div>
            <strong>+ AI spend</strong>
            <span>{legacyCatalog ? 'Published annual packs' : 'AI jobs + tokens at cost'}</span>
          </div>
          <div>
            <strong>+ Services</strong>
            <span>{legacyCatalog ? 'Implementation matching the edition' : 'Onboarding, success + FDE'}</span>
          </div>
        </div>
      </div>
    </SlideChrome>
  );
}

function SelectedPlanSlide({
  company,
  editionName,
  cur,
  validLabel,
  platformAnnual,
  currency,
  flows,
  flowsCustom,
  aiAnnual,
  aiCustom,
  aiName,
  termMonths,
  seatTerms,
  columns,
  confidential,
}: {
  company: string;
  editionName: string;
  cur: string;
  validLabel: string;
  platformAnnual: number | null;
  currency: string;
  flows?: number | null;
  flowsCustom?: boolean;
  aiAnnual: number | null;
  aiCustom?: boolean;
  aiName?: string;
  termMonths: number;
  seatTerms: string;
  columns: Array<{ title: string; items: string[] }>;
  confidential: string;
}) {
  return (
    <SlideChrome
      eyebrow={`Moxo ${editionName} plan (${cur})`}
      validLabel={validLabel}
      confidential={confidential}
    >
      <h3 className="sp-slide__title">{company} proposal plan</h3>
      <div className="sp-metrics">
        <div className="sp-metric">
          <p className="sp-metric__value">
            {platformAnnual != null ? money(platformAnnual, currency) : '—'}
          </p>
          <p className="sp-metric__label">
            Annual platform fee
            <br />
            {seatTerms.charAt(0).toUpperCase() + seatTerms.slice(1)} included
          </p>
        </div>
        <div className="sp-metric">
          <p className="sp-metric__value">
            {flowsCustom ? 'Custom' : flows != null ? flows.toLocaleString() : '—'}
          </p>
          <p className="sp-metric__label">
            Flows included / annually
          </p>
        </div>
        <div className="sp-metric">
          <p className="sp-metric__value">
            {aiCustom ? 'Custom' : aiAnnual != null ? money(aiAnnual, currency) : '—'}
          </p>
          <p className="sp-metric__label">
            {aiName ? `${aiName} / annually` : 'AI spend included / annually'}
          </p>
        </div>
        <div className="sp-metric">
          <p className="sp-metric__value">{termMonths} months</p>
          <p className="sp-metric__label">Contract duration</p>
        </div>
      </div>
      <div className="sp-featcols">
        {columns.map((col) => (
          <div key={col.title}>
            <p className="sp-featcols__title">{col.title}</p>
            <ul>
              {col.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </SlideChrome>
  );
}

function PlatformGridSlide({
  editions,
  matrix,
  confidential,
}: {
  editions: ProposalEditionSnapshot[];
  matrix: Array<{ label: string; flags: boolean[] }>;
  confidential: string;
}) {
  return (
    <SlideChrome eyebrow="Moxo platform" confidential={confidential}>
      <h3 className="sp-slide__title">Capability by plan</h3>
      <p className="sp-slide__lede">Availability follows the published catalog — not a custom menu.</p>
      <table className="sp-grid">
        <thead>
          <tr>
            <th>Capability</th>
            {editions.map((ed) => (
              <th key={ed.id}>{ed.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              {row.flags.map((on, i) => (
                <td key={editions[i]?.id ?? i} className={on ? 'is-on' : 'is-off'}>
                  {on ? 'Included' : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </SlideChrome>
  );
}

function IncludesSlide({
  cards,
  unlimitedSeats,
  aiJobs,
  currency,
  confidential,
}: {
  cards: Array<{ title: string; body: string }>;
  unlimitedSeats: boolean;
  aiJobs?: NonNullable<PricingProposal['catalog']>['aiJobs'];
  currency: string;
  confidential: string;
}) {
  const jobNote = aiJobs
    ? `AI spend covers every Moxo AI job, billed at cost from ${money(aiJobs.tiers[0]?.perJobMinor ?? 3, currency, true)} per job. ${aiJobs.tiers
        .map((t) => `${t.name} ${money(t.perJobMinor, currency, true)}`)
        .join(' · ')}.`
    : 'Published AI packs as quoted.';

  return (
    <SlideChrome eyebrow="Every plan includes" confidential={confidential}>
      <h3 className="sp-slide__title">
        {unlimitedSeats ? 'No surprises. No seat fees.' : 'No surprises.'}
      </h3>
      <div className="sp-includegrid">
        {cards.map((c) => (
          <article key={c.title} className="sp-include">
            <h4>{c.title}</h4>
            <p>{c.body}</p>
          </article>
        ))}
      </div>
      <div className="sp-aibar">
        <p>{jobNote}</p>
        <p className="sp-aibar__sub">{aiJobs?.neverBilled ?? 'Never billed: unpublished SKUs or invented unit rates.'}</p>
      </div>
    </SlideChrome>
  );
}

function AddonsSlide({
  usageAddons,
  aiAddons,
  portalAddons,
  currency,
  confidential,
}: {
  usageAddons: ReturnType<typeof addonsOf>;
  aiAddons: ReturnType<typeof addonsOf>;
  portalAddons: ReturnType<typeof addonsOf>;
  currency: string;
  confidential: string;
}) {
  const starterAi = aiAddons[0];
  return (
    <SlideChrome eyebrow="Add-ons / annual investment" confidential={confidential}>
      <h3 className="sp-slide__title">Additional capacity</h3>
      <p className="sp-slide__lede">
        Scale beyond your plan’s included flows. The per flow rate falls as volume grows.
      </p>
      <div className="sp-addons-layout">
        <table className="sp-grid sp-grid--tight">
          <thead>
            <tr>
              <th>Additional flows / yr</th>
              <th>Annual</th>
              <th>Per flow</th>
            </tr>
          </thead>
          <tbody>
            {usageAddons.map((a) => {
              const unit = perFlowMinor(a);
              return (
                <tr key={a.id}>
                  <td>{a.prepaidRuns ? a.prepaidRuns.toLocaleString() : a.name}</td>
                  <td>{a.listAnnualMinor != null ? money(a.listAnnualMinor, currency) : '—'}</td>
                  <td>{unit != null ? money(unit, currency, true) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="sp-addon-cards">
          {starterAi && starterAi.listAnnualMinor != null && (
            <article className="sp-sidecar">
              <p className="sp-sidecar__kicker">AI capacity</p>
              <p className="sp-sidecar__value">{money(starterAi.listAnnualMinor, currency)}</p>
              <p className="sp-sidecar__note">
                Per pack / year. Top up included AI spend as usage grows.
              </p>
            </article>
          )}
          {portalAddons.length > 0 && (
            <article className="sp-sidecar sp-sidecar--dark">
              <p className="sp-sidecar__kicker">Portal add-ons</p>
              <ul>
                {portalAddons.map((a) => (
                  <li key={a.id}>
                    <span>
                      {a.name}
                      {a.whoFor ? <small style={{ display: 'block', fontWeight: 400 }}>{a.whoFor}</small> : null}
                    </span>
                    <strong>
                      {a.listAnnualMinor != null ? money(a.listAnnualMinor, currency) : '—'}
                    </strong>
                  </li>
                ))}
              </ul>
            </article>
          )}
        </div>
      </div>
    </SlideChrome>
  );
}

function AiJobsSlide({
  aiJobs,
  currency,
  confidential,
}: {
  aiJobs: NonNullable<NonNullable<PricingProposal['catalog']>['aiJobs']>;
  currency: string;
  confidential: string;
}) {
  return (
    <SlideChrome eyebrow="AI spend / per job pricing" confidential={confidential}>
      <h3 className="sp-slide__title">Moxo AI jobs</h3>
      <p className="sp-slide__lede">{aiJobs.note}</p>
      <div className="sp-aitiers">
        {aiJobs.tiers.map((t) => (
          <article key={t.id} className="sp-aitier">
            <p className="sp-aitier__name">{t.name}</p>
            <p className="sp-aitier__price">{money(t.perJobMinor, currency, true)}</p>
            <p className="sp-aitier__note">Per job · {t.includes.join(', ')}</p>
          </article>
        ))}
      </div>
      <div className="sp-aibar">
        <p>{aiJobs.neverBilled}</p>
      </div>
    </SlideChrome>
  );
}

function ServicesSlide({
  lines,
  currency,
  confidential,
}: {
  lines: NonNullable<ReturnType<typeof quotedServiceLines>>;
  currency: string;
  confidential: string;
}) {
  return (
    <SlideChrome eyebrow="Implementation services" confidential={confidential}>
      <h3 className="sp-slide__title">Implementation, not a custom rate.</h3>
      <div className="sp-pilot">
        {lines.map((line) => (
          <div key={line.skuId}>
            <p className="sp-pilot__kicker">{line.name}</p>
            <p className="sp-pilot__price">
              {money(line.netMinor, currency)}
              {line.billing === 'one_time' ? ' one-time' : ''}
            </p>
            <p className="sp-pilot__note">
              {line.note ||
                'Quoted one-time implementation from the published catalog SKU on this pack.'}
            </p>
          </div>
        ))}
      </div>
    </SlideChrome>
  );
}

function InvestmentSlide({
  company,
  cur,
  validLabel,
  summary,
  currency,
  confidential,
}: {
  company: string;
  cur: string;
  validLabel: string;
  summary: ReturnType<typeof investmentSummary>;
  currency: string;
  confidential: string;
}) {
  return (
    <SlideChrome
      eyebrow={`${company} / investment summary (${cur})`}
      validLabel={validLabel}
      confidential={confidential}
    >
      <h3 className="sp-slide__title">Your Moxo investment</h3>
      <table className="sp-invest">
        <thead>
          <tr>
            <th>Item</th>
            <th>Detail</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {summary.rows.map((row) => (
            <tr key={row.skuId ?? `${row.item}:${row.detail}`}>
              <td>{row.item}</td>
              <td>{row.detail}</td>
              <td>{formatInvestmentCell(row, currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Total annual investment</th>
            <th>
              Recurring · {summary.termMonths}-month contract · {summary.seatTerms}
            </th>
            <th>{money(summary.annualRecurringMinor, currency)}</th>
          </tr>
          {summary.oneTimeMinor > 0 && (
            <tr className="sp-invest__onetime">
              <th>One-time services</th>
              <th>Not included in the annual total</th>
              <th>{money(summary.oneTimeMinor, currency)}</th>
            </tr>
          )}
        </tfoot>
      </table>
      <p className="sp-invest__fine">{investmentFinePrint(summary, currency, cur)}</p>
    </SlideChrome>
  );
}

function NotesSlide({
  note,
  adjustments,
  math,
  catalogVersion,
  effectiveDate,
  preparedWhen,
  confidential,
}: {
  note?: string;
  adjustments: PricingProposal['quote']['adjustments'];
  math?: string;
  catalogVersion: string;
  effectiveDate: string;
  preparedWhen: string;
  confidential: string;
}) {
  return (
    <SlideChrome eyebrow="Notes" confidential={confidential}>
      <h3 className="sp-slide__title">How this pack is built</h3>
      {adjustments.length > 0 && (
        <ul className="sp-adj">
          {adjustments.map((a) => (
            <li key={a.id}>
              <strong>{a.label}</strong> · {a.pct}% · {a.appliesTo}
            </li>
          ))}
        </ul>
      )}
      {math && <p className="sp-deck__math">{math}</p>}
      {note && <p className="sp-deck__prose">{note}</p>}
      <p className="sp-deck__math">
        Catalog {catalogVersion} · effective {effectiveDate} · prepared {preparedWhen}.
      </p>
    </SlideChrome>
  );
}
